import { defineStore } from "pinia";
import { normalizeOptimizationData, resolveMethod4Key, } from "@/utils/optimizationData";
import { logger } from "@/utils/logger";
import { pinia } from "./pinia";
import { useFrequencyStore } from "./frequency";
function getFrequencyStore() {
    return useFrequencyStore(pinia);
}
/**
 * Kök analiz / optimizasyon state (Pinia).
 * Okuma: `analysis.bridge.js`; yazma: Pinia actions (micro-8-2).
 */
export const useAppStore = defineStore("app", {
    state: () => ({
        analysisData: null,
        optimizationData: null,
        currentMethod: 4,
        selectedBeta: 0,
        activeScenarioId: "asim_0",
        loading: false,
        error: null,
    }),
    getters: {
        hasAnalysisData: (state) => !!state.analysisData,
        isLoading: (state) => state.loading,
        hasError: (state) => !!state.error,
        errorMessage: (state) => state.error,
    },
    actions: {
        setLoading(isLoading) {
            this.loading = isLoading;
        },
        setError(error) {
            this.error = error;
        },
        setAnalysisData(data) {
            this.analysisData = data;
        },
        setOptimizationData(data) {
            const incoming = data && typeof data === "object"
                ? data
                : null;
            const typeOutcome = incoming?.typeOutcome ?? null;
            const normalized = normalizeOptimizationData(data, this.analysisData);
            if (!normalized) {
                this.optimizationData = data ?? null;
                return;
            }
            this.optimizationData = typeOutcome
                ? { ...normalized, typeOutcome }
                : normalized;
        },
        setCurrentMethod(method) {
            this.currentMethod = method;
            if (method === 6)
                this.activeScenarioId = "mevcut";
            else if (method === 4) {
                const pct = Math.round(Number(this.selectedBeta) * 100);
                this.activeScenarioId = [0, 10, 20, 30].includes(pct) ? `asim_${pct}` : "asim_0";
            }
        },
        setBeta(beta) {
            this.selectedBeta = beta;
            if (this.currentMethod === 4) {
                const pct = Math.round(Number(beta) * 100);
                this.activeScenarioId = [0, 10, 20, 30].includes(pct) ? `asim_${pct}` : "asim_0";
            }
        },
        setActiveScenario(id) {
            this.activeScenarioId = id;
            if (id === "mevcut") {
                this.currentMethod = 6;
                return;
            }
            const match = String(id || "").match(/asim_(\d+)/);
            this.currentMethod = 4;
            this.selectedBeta = match ? Number(match[1]) / 100 : 0;
        },
        updateServices(services) {
            if (!this.analysisData)
                return;
            const servicesToUpdate = Array.isArray(services) ? services : [services];
            servicesToUpdate.forEach((service) => {
                const direction = service.yon;
                if (!this.analysisData[direction])
                    return;
                const serviceIndex = this.analysisData[direction].servisler?.findIndex((s) => s.id === service.id);
                if (serviceIndex > -1) {
                    this.analysisData[direction].servisler[serviceIndex] = {
                        ...service,
                    };
                }
            });
        },
        initializeApplication() {
            logger.debug("Uygulama başlatılıyor, optimization verileri kontrol ediliyor");
            if (!this.optimizationData) {
                console.warn("Optimizasyon verileri mevcut değil, planlar oluşturulmayacak");
                return Promise.resolve(false);
            }
            logger.debug("Tüm planlar ilk kez oluşturulacak");
            localStorage.removeItem("planStoreData");
            const optimizationData = JSON.parse(JSON.stringify(this.optimizationData));
            return new Promise((resolve) => {
                setTimeout(() => {
                    const frequencyStore = getFrequencyStore();
                    frequencyStore
                        .generateAllBasePlans(optimizationData)
                        .then(() => this.changeMethod(this.currentMethod || 1))
                        .then(() => {
                        logger.debug("Uygulama başarıyla başlatıldı");
                        resolve(true);
                    })
                        .catch((error) => {
                        logger.error("Uygulama başlatma hatası:", error);
                        resolve(false);
                    });
                }, 100);
            });
        },
        async changeMethod(method) {
            if (typeof method !== "number")
                return;
            const currentMethod = this.currentMethod;
            logger.debug(`Metod değişiyor: ${currentMethod} -> ${method}`);
            const frequencyStore = getFrequencyStore();
            try {
                const oldMethodKey = frequencyStore.getMethodKey;
                logger.debug("Mevcut metod anahtarı:", oldMethodKey);
                if (this.analysisData && method !== currentMethod) {
                    logger.debug("Metot değişikliği öncesi mevcut servis planı saklanıyor");
                }
                this.setCurrentMethod(method);
                const newMethodKey = frequencyStore.getMethodKey;
                logger.debug("Yeni metod anahtarı:", newMethodKey);
                const hasExistingPlan = frequencyStore.hasActivePlan(newMethodKey);
                if (!hasExistingPlan) {
                    logger.debug(`${newMethodKey} için yeni plan oluşturuluyor`);
                    await frequencyStore.generateBasePlan(newMethodKey);
                }
                else {
                    logger.debug(`${newMethodKey} için mevcut plan kullanılacak`);
                    await frequencyStore.applyUserModifications(newMethodKey);
                }
                // generateBasePlan / applyUserModifications zaten updateVehiclePlan (→ assignVehicles) çağırır
                window.dispatchEvent(new CustomEvent("service-plan-updated", {
                    detail: { methodKey: newMethodKey, forceRefresh: true },
                }));
                window.dispatchEvent(new CustomEvent("vehicle-plan-updated", {
                    detail: {
                        methodKey: newMethodKey,
                        forceRefresh: true,
                    },
                }));
                return { success: true, methodKey: newMethodKey };
            }
            catch (error) {
                logger.error("Method değişikliği hatası:", error);
                return { success: false, error: error.message };
            }
        },
        async changeBeta(beta) {
            if (beta === undefined || beta === null) {
                logger.warn("Tanımsız beta değeri, aşım yok (0) kullanılıyor");
                beta = 0;
            }
            const validBetas = [0, 0.1, 0.2, 0.3];
            if (!validBetas.includes(beta)) {
                logger.warn(`Geçersiz beta değeri düzeltildi, aşım yok kullanılıyor: ${beta}`);
                beta = 0;
            }
            const oldBeta = this.selectedBeta;
            logger.debug(`Beta değeri değişiyor: ${oldBeta} -> ${beta}`);
            this.setBeta(beta);
            if (this.currentMethod === 4 && this.analysisData) {
                const oldMethodKey = resolveMethod4Key(oldBeta);
                const newMethodKey = resolveMethod4Key(beta);
                if (oldMethodKey === newMethodKey) {
                    return { success: true, beta };
                }
                logger.debug(`Method key değişiyor: ${oldMethodKey} -> ${newMethodKey}`);
                const frequencyStore = getFrequencyStore();
                const hasExistingPlan = frequencyStore.hasActivePlan(newMethodKey);
                if (!hasExistingPlan) {
                    logger.debug(`${newMethodKey} için yeni plan oluşturuluyor`);
                    await frequencyStore.generateBasePlan(newMethodKey);
                }
                else {
                    logger.debug(`${newMethodKey} için mevcut plan kullanılacak`);
                    await frequencyStore.applyUserModifications(newMethodKey);
                }
                window.dispatchEvent(new CustomEvent("service-plan-updated", {
                    detail: { methodKey: newMethodKey, forceRefresh: true },
                }));
                window.dispatchEvent(new CustomEvent("vehicle-plan-updated", {
                    detail: {
                        methodKey: newMethodKey,
                        forceRefresh: true,
                    },
                }));
            }
            return { success: true, beta };
        },
    },
});
